<template>
  <v-container fluid class="pa-4">
    <v-row justify="space-between" align="center" class="mb-6">
      <v-col cols="12" sm="7">
        <h1 class="text-h4 font-weight-bold primary--text">
          <v-icon large color="primary" class="mr-3">mdi-golf</v-icon>
          {{ $t('golf_scorecards') || 'Scorecards de Golf' }}
        </h1>
        <div class="grey--text text--darken-1">
          {{ $t('scorecards_view_description') || 'Consulta tarjetas de juego por reservación o por jugador.' }}
        </div>
      </v-col>
      <v-col cols="12" sm="5" class="text-sm-right">
        <v-btn color="primary" rounded text class="mr-2" @click="$router.push('/golf/handicaps')">
          <v-icon left small>mdi-chart-line</v-icon>
          Control de handicaps
        </v-btn>
        <v-btn color="success" rounded class="mr-2 white--text" @click="openManualScorecard()">
          <v-icon left small>mdi-plus</v-icon>
          {{ $t('manual_scorecard')}}
        </v-btn>
        <v-btn color="primary" rounded outlined class="mr-2" @click="refreshData" :loading="loading">
          <v-icon left small>mdi-refresh</v-icon>
          {{ $t('actualizar') || 'Actualizar' }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="4">
        <v-text-field
          v-model="filters.search"
          :label="$t('buscar_jugador') || 'Buscar jugador / reservación / campo'"
          prepend-icon="mdi-magnify"
          outlined
          dense
          clearable
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <v-select
          v-model="filters.sortBy"
          :items="sortOptions"
          item-text="text"
          item-value="value"
          :label="$t('ordenar_por') || 'Ordenar por'"
          outlined
          dense
        ></v-select>
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <v-menu
          v-model="filters.menuDate"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="filters.date"
              :label="$t('fecha') || 'Fecha'"
              prepend-icon="mdi-calendar"
              readonly
              outlined
              dense
              clearable
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker v-model="filters.date" @input="filters.menuDate = false"></v-date-picker>
        </v-menu>
      </v-col>
    </v-row>

    <v-tabs v-model="activeTab" class="mb-6">
      <v-tab>
        <v-icon left small>mdi-calendar-check</v-icon>
        {{ $t('por_reservacion') || 'Por Reservación' }}
      </v-tab>
      <v-tab>
        <v-icon left small>mdi-account</v-icon>
        {{ $t('por_usuario') || 'Por Usuario' }}
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" class="transparent">
      <v-tab-item>
        <v-card class="elevation-2">
          <v-card-title>
            {{ $t('scorecards_por_reservacion') || 'Scorecards por Reservación' }}
            <v-spacer></v-spacer>
            <v-chip small color="primary" text-color="white">{{ filteredReservations.length }}</v-chip>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-data-table
              :headers="headersReservation"
              :items="filteredReservations"
              :loading="loading"
              :no-data-text="$t('no_records_yet') || 'Sin resultados'"
              class="elevation-0"
            >
              <template v-slot:item.date_played="{ item }">
                {{ formatDate(item.date_played) }}
              </template>
              <template v-slot:item.players_count="{ item }">
                <v-chip color="primary" text-color="white" small>
                  {{ item.players_count }}
                </v-chip>
              </template>
              <template v-slot:item.scorecards_count="{ item }">
                <v-chip :color="item.scorecards_count ? 'success' : 'grey'" text-color="white" small>
                  {{ item.scorecards_count }} / {{ item.players_count }}
                </v-chip>
                <v-chip v-if="item.in_progress_count" color="info" text-color="white" x-small class="ml-1">
                  {{ item.in_progress_count }} en juego
                </v-chip>
              </template>
              <template v-slot:item.player_names="{ item }">
                <div class="caption text-truncate" style="max-width: 280px;">
                  {{ item.player_names }}
                </div>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn icon small color="success" @click="openManualScorecard(item.reservation_id)">
                  <v-icon small>mdi-pencil-plus</v-icon>
                </v-btn>
                <v-btn v-if="item.scorecards_count" icon small color="primary" @click="viewReservationScorecard(item)">
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <v-tab-item>
        <v-card class="elevation-2">
          <v-card-title>
            {{ $t('scorecards_por_usuario') || 'Scorecards por Usuario' }}
            <v-spacer></v-spacer>
            <v-chip small color="primary" text-color="white">{{ filteredUsers.length }}</v-chip>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-data-table
              :headers="headersUser"
              :items="filteredUsers"
              :loading="loading"
              :no-data-text="$t('no_records_yet') || 'Sin resultados'"
              class="elevation-0"
            >
              <template v-slot:item.date_played="{ item }">
                {{ formatDate(item.date_played) }}
              </template>
              <template v-slot:item.tee_name="{ item }">
                <v-chip v-if="item.tee_name" small outlined>
                  {{ item.tee_name }}
                </v-chip>
                <span v-else>--</span>
              </template>
              <template v-slot:item.score_label="{ item }">
                <v-chip :color="getScoreColor(item.score_vs_par)" dark small>
                  {{ item.score_label }}
                </v-chip>
              </template>
              <template v-slot:item.score_vs_par="{ item }">
                <span :class="getScoreClass(item.score_vs_par)">
                  {{ item.score_vs_par > 0 ? '+' : '' }}{{ item.score_vs_par }}
                </span>
              </template>
              <template v-slot:item.handicap_round="{ item }">
                <v-chip v-if="item.handicap_round" small :color="handicapStatusColor(item.handicap_round.status)" text-color="white">
                  {{ handicapStatusLabel(item.handicap_round.status) }}
                </v-chip>
                <span v-else class="grey--text">No vinculada</span>
              </template>
              <template v-slot:item.lifecycle_status="{ item }">
                <v-chip small :color="scorecardStatusColor(item.lifecycle_status)" text-color="white">
                  {{ scorecardStatusLabel(item.lifecycle_status) }}
                </v-chip>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn icon small color="success" @click="openManualScorecard(item.reservation_id, item.id)">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon small color="primary" @click="viewUserScorecard(item)">
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
                <v-btn
                  v-if="!item.handicap_round || item.handicap_round.status === 'draft'"
                  icon
                  small
                  color="error"
                  @click="deleteScorecard(item)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog v-model="dialogScorecard" max-width="960px" scrollable>
      <v-card>
        <v-card-title class="primary--text">
          <v-icon left color="primary">mdi-golf</v-icon>
          {{ currentDialogTitle }}
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <v-row v-if="dialogLoading" justify="center" class="py-8">
            <v-progress-circular indeterminate color="primary" size="42"></v-progress-circular>
          </v-row>

          <template v-else>
            <v-row v-if="reservationPlayers.length > 1" class="mb-4">
              <v-col cols="12" md="6">
                <v-select
                  v-model="selectedScorecard"
                  :items="reservationPlayers"
                  item-text="player_name"
                  return-object
                  :label="$t('jugador') || 'Jugador'"
                  outlined
                  dense
                ></v-select>
              </v-col>
            </v-row>

            <v-row class="mb-6">
              <v-col cols="12" sm="6">
                <div class="mb-3">
                  <span class="font-weight-bold grey--text">{{ $t('jugador') || 'Jugador' }}:</span>
                  <span class="ml-2">{{ selectedScorecard.player_name }}</span>
                </div>
                <div class="mb-3">
                  <span class="font-weight-bold grey--text">{{ $t('handicap') || 'Handicap' }}:</span>
                  <span class="ml-2 title font-weight-bold primary--text">{{ selectedScorecard.handicap }}</span>
                </div>
                <div v-if="selectedScorecard.tee" class="mb-3">
                  <span class="font-weight-bold grey--text">{{ $t('tee') || 'Tee' }}:</span>
                  <v-chip small :color="getTeeColor(selectedScorecard.tee.color)">
                    {{ selectedScorecard.tee.name }}
                  </v-chip>
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="mb-3">
                  <span class="font-weight-bold grey--text">{{ $t('fecha') || 'Fecha' }}:</span>
                  <span class="ml-2">{{ formatDate(selectedScorecard.date_played) }}</span>
                </div>
                <div class="mb-3">
                  <span class="font-weight-bold grey--text">{{ $t('salida') || 'Salida' }}:</span>
                  <span class="ml-2">
                    {{ selectedScorecard.course_name }}
                    <AgreementBadge
                      v-if="selectedAgreement"
                      :agreement="selectedAgreement"
                      class="ml-2"
                    />
                  </span>
                </div>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-card flat class="mb-4">
                  <v-card-title class="subtitle-2">{{ $t('primeros_nueve') || 'Primeros 9 Hoyos' }}</v-card-title>
                  <v-simple-table dense class="mb-4">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>Hoyo</th>
                          <th>Par</th>
                          <th>Golpes</th>
                          <th>+/-</th>
                          <th>Etiqueta</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="hole in selectedScorecard.first_nine.holes" :key="`front-${hole.hole_number}`">
                          <td class="font-weight-bold">{{ hole.hole_number }}</td>
                          <td>{{ hole.par }}</td>
                          <td class="font-weight-bold">{{ hole.strokes }}</td>
                          <td :class="getScoreClass(hole.score_vs_par)">
                            {{ hole.score_vs_par > 0 ? '+' : '' }}{{ hole.score_vs_par }}
                          </td>
                          <td>{{ hole.score_label }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                  <v-divider></v-divider>
                  <div class="pa-3">
                    <v-row dense>
                      <v-col cols="6" class="font-weight-bold">{{ $t('par_total') || 'Par Total' }}:</v-col>
                      <v-col cols="6" class="text-right font-weight-bold">{{ selectedScorecard.first_nine.par_total }}</v-col>
                      <v-col cols="6" class="font-weight-bold">{{ $t('golpes') || 'Golpes' }}:</v-col>
                      <v-col cols="6" class="text-right font-weight-bold primary--text">{{ selectedScorecard.first_nine.strokes_total }}</v-col>
                      <v-col cols="6" class="font-weight-bold">vs Par:</v-col>
                      <v-col cols="6" class="text-right font-weight-bold" :class="getScoreClass(selectedScorecard.first_nine.score_vs_par)">
                        {{ selectedScorecard.first_nine.score_vs_par > 0 ? '+' : '' }}{{ selectedScorecard.first_nine.score_vs_par }}
                      </v-col>
                    </v-row>
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card flat class="mb-4">
                  <v-card-title class="subtitle-2">{{ $t('segunda_vuelta') || 'Segunda Vuelta (10-18)' }}</v-card-title>
                  <v-simple-table dense class="mb-4">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>Hoyo</th>
                          <th>Par</th>
                          <th>Golpes</th>
                          <th>+/-</th>
                          <th>Etiqueta</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="hole in selectedScorecard.second_nine.holes" :key="`back-${hole.hole_number}`">
                          <td class="font-weight-bold">{{ hole.hole_number }}</td>
                          <td>{{ hole.par }}</td>
                          <td class="font-weight-bold">{{ hole.strokes }}</td>
                          <td :class="getScoreClass(hole.score_vs_par)">
                            {{ hole.score_vs_par > 0 ? '+' : '' }}{{ hole.score_vs_par }}
                          </td>
                          <td>{{ hole.score_label }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                  <v-divider></v-divider>
                  <div class="pa-3">
                    <v-row dense>
                      <v-col cols="6" class="font-weight-bold">{{ $t('par_total') || 'Par Total' }}:</v-col>
                      <v-col cols="6" class="text-right font-weight-bold">{{ selectedScorecard.second_nine.par_total }}</v-col>
                      <v-col cols="6" class="font-weight-bold">{{ $t('golpes') || 'Golpes' }}:</v-col>
                      <v-col cols="6" class="text-right font-weight-bold primary--text">{{ selectedScorecard.second_nine.strokes_total }}</v-col>
                      <v-col cols="6" class="font-weight-bold">vs Par:</v-col>
                      <v-col cols="6" class="text-right font-weight-bold" :class="getScoreClass(selectedScorecard.second_nine.score_vs_par)">
                        {{ selectedScorecard.second_nine.score_vs_par > 0 ? '+' : '' }}{{ selectedScorecard.second_nine.score_vs_par }}
                      </v-col>
                    </v-row>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <v-row class="mt-6">
              <v-col cols="12">
                <v-card flat class="grey lighten-4 rounded-lg">
                  <v-card-text class="py-5">
                    <v-row>
                      <v-col cols="12" sm="3" class="text-center">
                        <div class="caption text-uppercase grey--text text--darken-1 font-weight-bold mb-1">{{ $t('par_total') || 'Par Total' }}</div>
                        <div class="display-1 font-weight-bold grey--text text--darken-3">{{ selectedScorecard.summary.total_par }}</div>
                      </v-col>
                      <v-col cols="12" sm="3" class="text-center">
                        <div class="caption text-uppercase grey--text text--darken-1 font-weight-bold mb-1">{{ $t('golpes') || 'Golpes' }}</div>
                        <div class="display-1 font-weight-bold grey--text text--darken-3">{{ selectedScorecard.summary.total_strokes }}</div>
                      </v-col>
                      <v-col cols="12" sm="3" class="text-center">
                        <div class="caption text-uppercase grey--text text--darken-1 font-weight-bold mb-1">{{ $t('neto') || 'Net Score' }}</div>
                        <div class="display-1 font-weight-bold primary--text">{{ selectedScorecard.summary.net_score }}</div>
                      </v-col>
                      <v-col cols="12" sm="3" class="text-center">
                        <div class="caption text-uppercase grey--text text--darken-1 font-weight-bold mb-1">vs Par</div>
                        <div class="display-1 font-weight-bold" :class="getScoreClass(selectedScorecard.summary.score_vs_par)">
                          {{ selectedScorecard.summary.score_vs_par > 0 ? '+' : '' }}{{ selectedScorecard.summary.score_vs_par }}
                        </div>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </template>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="success" @click="openDownloadModal">
            <v-icon left>mdi-download</v-icon>
            {{ $t('download_scorecard') }}
          </v-btn>
          <v-btn color="primary" text @click="dialogScorecard = false">
            {{ $t('cerrar') || 'Cerrar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Download Scorecard Modal -->
    <DownloadScorecardModal
      :show="showDownloadModal"
      :reservation="selectedScorecard"
      :agreement="selectedAgreement"
      @close="showDownloadModal = false"
    />

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <ManualScorecardModal
      :show="showManualModal"
      :reservations="reservations"
      :initial-reservation-id="manualReservationId"
      :initial-score-id="manualScoreId"
      @update:show="showManualModal = $event"
      @save="saveManualScore"
    />

  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  fetchScorecardCaptureContext,
  fetchReservationScorecardGroup,
  fetchUserScorecard,
  getDateRange,
  normalizeScorecard,
  apiCall
} from '@/utils/golfScorecards'
import ManualScorecardModal from './components/ManualScorecardModal.vue'
import AgreementBadge from './components/AgreementBadge.vue'
import DownloadScorecardModal from './components/DownloadScorecardModal.vue'

export default {
  name: 'GolfScorecards',
  components: {
    ManualScorecardModal,
    AgreementBadge,
    DownloadScorecardModal
  },
  data () {
    return {
      loading: false,
      dialogLoading: false,
      activeTab: 0,
      dialogScorecard: false,
      showManualModal: false,
      manualReservationId: null,
      manualScoreId: null,
      showDownloadModal: false,
      selectedScorecard: normalizeScorecard({}),
      selectedAgreement: null,
      reservationPlayers: [],
      groupsByReservation: {},
      filters: {
        search: '',
        date: null,
        menuDate: false,
        sortBy: 'date'
      },
      sortOptions: [
        { text: 'Fecha (más reciente)', value: 'date' },
        { text: 'Jugador A-Z', value: 'player' },
        { text: 'Mejor score', value: 'score' }
      ],
      headersReservation: [
        { text: 'Fecha', value: 'date_played', sortable: false },
        { text: 'Salida', value: 'course', sortable: false },
        { text: 'Jugadores', value: 'players_count', sortable: false },
        { text: 'Tarjetas', value: 'scorecards_count', sortable: false },
        { text: 'Participantes', value: 'player_names', sortable: false },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
      ],
      headersUser: [
        { text: 'Fecha', value: 'date_played', sortable: false },
        { text: 'Jugador', value: 'player_name', sortable: false },
        { text: 'Salida', value: 'course_name', sortable: false },
        { text: 'Tee', value: 'tee_name', sortable: false },
        { text: 'Handicap', value: 'handicap', sortable: false },
        { text: 'Puntuación', value: 'score_label', sortable: false },
        { text: '+/-', value: 'score_vs_par', sortable: false },
        { text: 'Estado tarjeta', value: 'lifecycle_status', sortable: false },
        { text: 'Estado HCP', value: 'handicap_round', sortable: false },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
      ],
      reservations: [],
      users: [],
      snackbar: {
        show: false,
        text: '',
        color: 'info'
      }
    }
  },
  computed: {
    ...mapGetters(['getProfile']),
    housingUnitId () {
      return this.getProfile.idHousingUnit || localStorage.getItem('idHousingUnit')
    },
    currentLanguage () {
      return (this.$i18n && this.$i18n.locale) || 'es'
    },
    filteredReservations () {
      const search = (this.filters.search || '').toLowerCase().trim()
      let items = this.reservations.filter(item => this.matchesDateFilter(item.date_played))

      if (search) {
        items = items.filter(item => {
          return [item.course, item.player_names, item.display_user, item.reservation_id]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(search)
        })
      }

      return items.sort((left, right) => new Date(right.date_played) - new Date(left.date_played))
    },
    filteredUsers () {
      const search = (this.filters.search || '').toLowerCase().trim()
      let items = this.users.filter(item => this.matchesDateFilter(item.date_played))

      if (search) {
        items = items.filter(item => {
          return [item.player_name, item.course_name, item.tee_name, item.reservation_id]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(search)
        })
      }

      if (this.filters.sortBy === 'player') {
        return items.sort((left, right) => left.player_name.localeCompare(right.player_name))
      }

      if (this.filters.sortBy === 'score') {
        return items.sort((left, right) => left.score_vs_par - right.score_vs_par)
      }

      return items.sort((left, right) => new Date(right.date_played) - new Date(left.date_played))
    },
    currentDialogTitle () {
      if (this.reservationPlayers.length > 1) {
        return this.$t('scorecards_por_reservacion') || 'Scorecards por Reservación'
      }

      return `Scorecard - ${this.selectedScorecard.player_name || '--'}`
    }
  },
  mounted () {
    this.loadScorecards()
  },
  methods: {
    async loadScorecards () {
      if (!this.housingUnitId) {
        this.showSnackbar('No se encontraron registros de usuarios', 'error')
        return
      }

      this.loading = true
      try {
        const range = getDateRange(120, 1)
        const dataset = await fetchScorecardCaptureContext({
          housingUnitId: this.housingUnitId,
          language: this.currentLanguage,
          startDate: range.start,
          endDate: range.end
        })

        this.reservations = dataset.reservations
        this.users = dataset.userScores
        this.groupsByReservation = dataset.groupsByReservation
      } catch (error) {
        console.error('Error fetching golf scorecards:', error)
        this.showSnackbar('No fue posible cargar los scorecards de golf.', 'error')
      } finally {
        this.loading = false
      }
    },
    async viewReservationScorecard (reservation) {
      this.dialogScorecard = true
      this.dialogLoading = true

      try {
        let players = this.groupsByReservation[reservation.reservation_id]

        if (!players) {
          const group = await fetchReservationScorecardGroup(reservation.reservation_id, this.currentLanguage, reservation)
          players = group.players
          this.$set(this.groupsByReservation, reservation.reservation_id, players)
        }

        this.reservationPlayers = players
        this.selectedScorecard = players.length ? players[0] : normalizeScorecard({})
        this.loadAgreementForScorecard()
      } catch (error) {
        console.error('Error loading reservation scorecard:', error)
        this.reservationPlayers = []
        this.selectedScorecard = normalizeScorecard({})
        this.selectedAgreement = null
        this.showSnackbar('No fue posible cargar el scorecard de la reservación.', 'error')
      } finally {
        this.dialogLoading = false
      }
    },
    async viewUserScorecard (user) {
      this.dialogScorecard = true
      this.dialogLoading = true
      this.reservationPlayers = []

      try {
        this.selectedScorecard = await fetchUserScorecard(user.id, this.currentLanguage)
        this.loadAgreementForScorecard()
      } catch (error) {
        console.error('Error loading user scorecard:', error)
        this.selectedScorecard = normalizeScorecard({})
        this.selectedAgreement = null
        this.showSnackbar('No fue posible cargar el scorecard del jugador.', 'error')
      } finally {
        this.dialogLoading = false
      }
    },
    loadAgreementForScorecard () {
      if (!this.selectedScorecard || !this.selectedScorecard.course_name) {
        this.selectedAgreement = null
        return
      }

      const courseName = this.selectedScorecard.course_name
      const allAgreements = this.$store.state.courseAgreements?.agreements || []
      this.selectedAgreement = allAgreements.find(
        a => a.name === courseName && a.is_active
      ) || null
    },
    openDownloadModal () {
      this.showDownloadModal = true
    },
    refreshData () {
      this.loadScorecards()
    },
    openManualScorecard (reservationId = null, scoreId = null) {
      this.manualReservationId = reservationId
      this.manualScoreId = scoreId
      this.showManualModal = true
    },
    clearFilters () {
      this.filters = {
        search: '',
        date: null,
        menuDate: false,
        sortBy: 'date'
      }
    },
    matchesDateFilter (dateTime) {
      if (!this.filters.date) {
        return true
      }

      return String(dateTime || '').slice(0, 10) === this.filters.date
    },
    formatDate (date) {
      if (!date) return '--'

      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    getScoreColor (scoreVsPar) {
      if (scoreVsPar === null || typeof scoreVsPar === 'undefined') return 'grey'
      if (scoreVsPar < -1) return 'primary'
      if (scoreVsPar === -1) return 'success'
      if (scoreVsPar === 0) return 'info'
      if (scoreVsPar === 1) return 'warning'
      return 'error'
    },
    getScoreClass (scoreVsPar) {
      if (scoreVsPar === null || typeof scoreVsPar === 'undefined') return 'grey--text'
      if (scoreVsPar < -1) return 'primary--text'
      if (scoreVsPar === -1) return 'success--text'
      if (scoreVsPar === 0) return 'info--text'
      if (scoreVsPar === 1) return 'warning--text'
      return 'error--text'
    },
    getTeeColor (color) {
      const colors = {
        blue: '#1E88E5',
        white: '#FFFFFF',
        gold: '#F9A825',
        red: '#E53935'
      }

      return colors[color] || 'grey'
    },
    handicapStatusColor (status) {
      return { draft: 'warning', validated: 'success', invalidated: 'error' }[status] || 'grey'
    },
    handicapStatusLabel (status) {
      return { draft: 'Pendiente de validar', validated: 'Validada', invalidated: 'Anulada' }[status] || status
    },
    scorecardStatusColor (status) {
      return { in_progress: 'info', completed: 'success', locked: 'grey darken-2' }[status] || 'grey'
    },
    scorecardStatusLabel (status) {
      return { in_progress: 'En juego', completed: 'Completa', locked: 'Bloqueada' }[status] || status
    },
    showSnackbar (text, color) {
      this.snackbar = {
        show: true,
        text,
        color
      }
    },
    async saveManualScore ({ payload, scoreId }) {
      this.loading = true
      try {
        const method = scoreId ? 'put' : 'post'
        const endpoint = scoreId ? `/golf-scores/${scoreId}/` : '/golf-scores/'
        await apiCall(method, endpoint, this.currentLanguage, payload)
        this.showSnackbar(scoreId ? 'Scorecard actualizado correctamente.' : 'Scorecard registrado correctamente.', 'success')
        await this.loadScorecards()
      } catch (error) {
        console.error('Error al guardar scorecard:', error)
        const detail = error.response?.data?.handicap || error.response?.data?.detail
        this.showSnackbar(Array.isArray(detail) ? detail.join(' ') : (detail || 'Error al guardar el scorecard.'), 'error')
      } finally {
        this.loading = false
      }
    },
    async deleteScorecard (score) {
      if (!window.confirm(`¿Eliminar la tarjeta de ${score.player_name}?`)) return
      this.loading = true
      try {
        await apiCall('delete', `/golf-scores/${score.id}/`, this.currentLanguage)
        this.showSnackbar('Scorecard eliminado correctamente.', 'success')
        await this.loadScorecards()
      } catch (error) {
        const detail = error.response?.data?.handicap || error.response?.data?.detail
        this.showSnackbar(Array.isArray(detail) ? detail.join(' ') : (detail || 'No fue posible eliminar el scorecard.'), 'error')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
